import * as XLSX from "xlsx";
import { format as dateFnsFormat, parseISO } from "date-fns";
import { formatMapPlotRequestDateTime } from "@/features/mapPlotRequest/utils/dateTime";
import { type SavedMapPlotRequestDetail } from "@/features/map/types";
import { type AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";
import { parseAdditionalFields } from "@/features/map/utils/additionalFieldsHelpers";

/**
 * 物件リスト（プロット詳細データ）をCSVまたはExcel形式でダウンロードする共通関数
 *
 * @param formatType - ダウンロードするファイル形式 ('csv' または 'excel')
 * @param listName - リスト全体の識別名（ファイル名のプレフィックスに使用）
 * @param details - エクスポート対象となる物件（プロット）詳細データの配列
 * @param availableFields - 管理画面等で設定されたカスタム（追加）フィールドの設定情報
 */
export const exportPlotRequestDetails = (
  formatType: "csv" | "excel",
  listName: string,
  details: SavedMapPlotRequestDetail[],
  availableFields?: AdditionalFieldConfig
): void => {
  // ダウンロード対象データが空の場合は処理を行わない
  if (details.length === 0) {
    return;
  }

  // 画面（DataGrid）のテーブル表示順に合わせて、エクスポート用ヘッダー（列見出し）を動的に構築
  const headers: string[] = [
    "ステータス",
    "種別",
    "住居表示",
    "所在",
    "地番・家屋番号",
    "緯度",
    "経度",
    "タグ",
    "備考",
  ];

  // 管理画面で定義されたアクティブなカスタム項目を追加
  const activeFields = availableFields?.fields ?? [];
  activeFields.forEach((field) => {
    headers.push(field.key);
  });

  // 末尾に作成・更新日時を付与
  headers.push("作成日時", "更新日時");

  // 物件リストのデータ行を2次元配列にマッピング
  const dataRows: string[][] = details.map((detail) => {
    // 種別が建物・区分建物の場合は家屋番号、土地等の場合は地番を出力する
    const bookType = detail.realEstateType ?? "土地";
    const isBuilding = bookType === "建物" || bookType === "区分建物";
    const chibanOrKaoku = isBuilding
      ? detail.requestKaokuNumber
      : detail.requestChiban;

    // ステータスコード（英語）を日本語の表示名に変換
    const statusStr =
      detail.status === "SUCCEED"
        ? "成功"
        : detail.status === "FAILED"
        ? "失敗"
        : String(detail.status);

    // 基本データ項目の格納
    const row: string[] = [
      statusStr,
      detail.realEstateType ?? "",
      detail.requestAddress ?? "",
      detail.requestLocation ?? "",
      chibanOrKaoku ?? "",
      detail.substituteLatitude !== null
        ? String(detail.substituteLatitude)
        : "",
      detail.substituteLongitude !== null
        ? String(detail.substituteLongitude)
        : "",
      detail.tags.map((t) => t.name).join(", "),
      detail.remarks ?? "",
    ];

    // 各物件のカスタム（追加）項目の値をパースし、設定された型に基づいてフォーマットして格納
    if (activeFields.length > 0) {
      const parsedFields = parseAdditionalFields(detail.additionalFields);
      activeFields.forEach((field) => {
        let value = parsedFields[field.id] ?? "";
        if (value && typeof value === "string") {
          try {
            if (field.fieldType === "date") {
              const parsed = parseISO(value);
              if (!isNaN(parsed.getTime())) {
                value = dateFnsFormat(parsed, "yyyy/MM/dd");
              }
            } else if (field.fieldType === "datetime") {
              const parsed = parseISO(value);
              if (!isNaN(parsed.getTime())) {
                value = dateFnsFormat(parsed, "yyyy/MM/dd HH:mm");
              }
            } else if (field.fieldType === "boolean") {
              value = value === "true" ? "はい" : "いいえ";
            }
          } catch {
            // 日付パース失敗時などは、生の文字列値をそのまま出力
          }
        } else if (field.fieldType === "boolean") {
          // boolean 型で値が未設定の場合は「いいえ」とする
          value = "いいえ";
        }
        row.push(value);
      });
    }

    // 作成日時、更新日時をフォーマットして追加
    row.push(
      formatMapPlotRequestDateTime(detail.createdAt, ""),
      formatMapPlotRequestDateTime(detail.updatedAt, "")
    );

    return row;
  });

  // xlsx ライブラリを用いてシートおよびワークブックオブジェクトを生成
  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "物件リスト");

  /**
   * OSやファイルシステム上で使用できない禁止文字をアンダースコアに置換する関数
   */
  const sanitizeFileName = (name: string): string => {
    return name.replace(/[\\/:*?"<>|]/g, "_");
  };

  const sanitizedName = sanitizeFileName(listName || "物件リスト");
  const dateStr = dateFnsFormat(new Date(), "yyyyMMddHHmmss");

  // 出力ファイル名のベース（接頭辞_リスト名_日時タイムスタンプ）
  const baseFileName = `物件リスト_${sanitizedName}_${dateStr}`;

  if (formatType === "excel") {
    // xlsx のビルトイン書き出し機能を用いてダウンロード処理を実行
    XLSX.writeFile(workbook, `${baseFileName}.xlsx`);
  } else {
    // UTF-8 CSVに変換し、Excel等での文字化けを防ぐためBOM（Byte Order Mark）を付与してダウンロードを実行
    const csvContent = XLSX.utils.sheet_to_csv(worksheet);
    const bom = new Uint8Array([0xef, 0xbb, 0xbf]);
    const blob = new Blob([bom, csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${baseFileName}.csv`);

    // DOMに一時的に追加してクリックイベントを発火させ、ダウンロードをトリガーする
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};
